// Squelette MakeHuman "default" (163 os) : construction de la hiérarchie THREE.Bone et ajustement
// des positions de repos sur le maillage morphé (sémantique de shared/skeleton.py).
//
// Orientation de repos (convention MakeHuman/Blender) : axe Y local = tête -> queue, axe X = normale
// moyenne des plans de roll (rotation_plane), Z = X × Y. Matrice globale de repos :
//   Y = norm(queue - tête) ; Z = norm(normale × Y) ; X = norm(Y × Z) ; origine = tête.
import * as THREE from 'three';
import type { SkeletonDef } from './format';

const _x = new THREE.Vector3(), _y = new THREE.Vector3(), _z = new THREE.Vector3(), _n = new THREE.Vector3();
const _h = new THREE.Vector3(), _t = new THREE.Vector3(), _a = new THREE.Vector3(), _b = new THREE.Vector3();
const _p = new THREE.Vector3(), _s = new THREE.Vector3(), _q = new THREE.Quaternion(), _qi = new THREE.Quaternion();
const _m = new THREE.Matrix4();

export class HumanSkeleton {
  readonly bones: THREE.Bone[] = [];
  readonly byName = new Map<string, THREE.Bone>();
  readonly roots: THREE.Bone[] = [];
  readonly skeleton: THREE.Skeleton;
  /** Matrices globales de repos (repère de Human.root), une par os. */
  readonly restGlobal: THREE.Matrix4[] = [];
  /** Positions des joints (repère root), 3 floats par joint. */
  readonly jointPos: Float32Array;
  private fitted = false;

  constructor(readonly def: SkeletonDef) {
    for (const b of def.bones) {
      const bone = new THREE.Bone();
      bone.name = b.name;
      bone.userData.restQuaternion = new THREE.Quaternion();
      bone.userData.restPosition = new THREE.Vector3();
      this.bones.push(bone);
      this.byName.set(b.name, bone);
      this.restGlobal.push(new THREE.Matrix4());
    }
    def.bones.forEach((b, i) => {
      if (b.parent >= 0) this.bones[b.parent].add(this.bones[i]);
      else this.roots.push(this.bones[i]);
    });
    const inverses = def.bones.map(() => new THREE.Matrix4());
    this.skeleton = new THREE.Skeleton(this.bones, inverses);
    this.jointPos = new Float32Array(def.joints.length * 3);
  }

  /**
   * Recalcule les joints et l'orientation de repos depuis les positions de base morphées (mètres).
   * Conserve les mêmes objets Bone ; une pose éventuellement appliquée (quaternion/position différents
   * du repos) est préservée relativement au nouveau repos. Met à jour boneInverses.
   */
  fit(positions: Float32Array) {
    const { def, jointPos } = this;
    def.joints.forEach((verts, j) => {
      let x = 0, y = 0, z = 0;
      for (const v of verts) {
        x += positions[v * 3]; y += positions[v * 3 + 1]; z += positions[v * 3 + 2];
      }
      const k = verts.length || 1;
      jointPos[j * 3] = x / k; jointPos[j * 3 + 1] = y / k; jointPos[j * 3 + 2] = z / k;
    });
    const J = (j: number, out: THREE.Vector3) => out.fromArray(jointPos, j * 3);

    def.bones.forEach((b, i) => {
      J(b.head, _h);
      J(b.tail, _t);
      // normale = moyenne des normales (non nulles) des plans de roll
      _n.set(0, 0, 0);
      let count = 0;
      for (const [j1, j2, j3] of b.planes) {
        J(j1, _a); J(j2, _b); J(j3, _p);
        _s.subVectors(_b, _a).normalize();   // pvec = p2 - p1
        _p.sub(_b).normalize();              // yvec = p3 - p2
        _p.cross(_s);                        // normale = yvec × pvec
        if (_p.lengthSq() > 1e-12) {
          _n.add(_p.normalize());
          count++;
        }
      }
      if (count === 0 || _n.lengthSq() < 1e-10) _n.set(0, 1, 0);
      else _n.divideScalar(count);
      _y.subVectors(_t, _h).normalize();
      _z.crossVectors(_n, _y).normalize();
      _x.crossVectors(_y, _z).normalize();
      this.restGlobal[i].makeBasis(_x, _y, _z).setPosition(_h);
    });

    def.bones.forEach((b, i) => {
      const bone = this.bones[i];
      // repère local de repos = parent⁻¹ · global
      if (b.parent >= 0) _m.copy(this.restGlobal[b.parent]).invert().multiply(this.restGlobal[i]);
      else _m.copy(this.restGlobal[i]);
      _m.decompose(_p, _q, _s);
      const rq = bone.userData.restQuaternion as THREE.Quaternion;
      const rp = bone.userData.restPosition as THREE.Vector3;
      if (this.fitted) {
        // pose courante relative au repos : q = repos · pose  =>  pose = repos⁻¹ · q
        _qi.copy(rq).invert().multiply(bone.quaternion);
        bone.quaternion.copy(_q).multiply(_qi);
        bone.position.sub(rp).add(_p);
      } else {
        bone.quaternion.copy(_q);
        bone.position.copy(_p);
      }
      rq.copy(_q);
      rp.copy(_p);
      this.skeleton.boneInverses[i].copy(this.restGlobal[i]).invert();
    });
    this.fitted = true;
  }

  /** Remet tous les os dans leur pose de repos. */
  resetPose() {
    for (const bone of this.bones) {
      bone.quaternion.copy(bone.userData.restQuaternion);
      bone.position.copy(bone.userData.restPosition);
    }
  }
}
